/* eslint-disable react-hooks/exhaustive-deps */
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    styled,
    TextField,
    Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { LoadingPage } from '../../../../common/LoadingPage'
import { ClearIcon } from '@mui/x-date-pickers'
import { ActionStage, ArchitecturalCode, CurrencyJSON, FundingSource, ItemStatus, PriceType, Responsibility, Scope } from '../../../../common/const'
import { showNotification } from '../../../../common/Notification/ToastifyNotification'
import { apiRequest } from '../../../../common/common'
import moment from 'moment'

const DemoBox1 = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '5px 5px 5px 15px'
}))

const UpdateItem = ({ setItemOpen, selectedData, projectData, onGridReady }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [manufacturerData, setManufacturerData] = useState([]);
    const [manufacturerLoading, setManufacturerLoading] = useState(false);
    const [modelLoading, setModelLoading] = useState(false);
    const [modelData, setModelData] = useState([]);
    const [phaseData, setPhaseData] = useState([]);
    const [phaseLoading, setPhaseLoading] = useState(false);
    const [costData, setCostData] = useState([]);
    const [costLoading, setCostLoading] = useState([]);
    const [budgetLoading, setBudgetLoading] = useState(false);
    const [budgetData, setBudgetData] = useState([]);
    const [isConvert, setIsConvert] = useState(false);
    const [vendorLoading, setVendorLoading] = useState(false);
    const [vendorData, setVendorData] = useState([]);
    const [vendorPage, setVendorPage] = useState(1);
    const [isVendorAvailable, setIsVendorAvailable] = useState(true);

    // Tracks which fields the user has explicitly chosen to clear on
    // save. Kept separate from itemData so we can distinguish
    // "untouched" (null in itemData, not in resetFields) from
    // "deliberately reset" (null in itemData, true in resetFields).
    // Add more keys here when you need a Reset toggle on more fields.
    const [resetFields, setResetFields] = useState({
        alternate_description: false,
    });

    const [itemData, setItemData] = useState({
        manufacturer_id: null,
        need_by_date: null,
        quantity: null,
        unit_price: null,
        funding_source: null,
        alternate_description: null,
        status: null,
        scope: null,
        product_id: null,
        notes: null,
        budget_id: null,
        phase_id: null,
        phase_date: null,
        bid_group_id: null,
        cost_centre_id: null,
        stage: null,
        product_data: {
            price_type: null,
            model: null,
            custom_id_1: null,
            custom_id_2: null,
            furnish_responsibility: null,
            install_responsibility: null,
            architectural_code: null,
            vendor_id: null,
        }
    })

    const uniqueManufacturers = Object.values(
        manufacturerData.reduce((acc, { manufacturer }) => {
            acc[manufacturer.product_provider_id] = manufacturer;
            return acc;
        }, {})
    );

    const getVendorData = async (pageNo, searchText) => {
        try {
            setVendorLoading(true);
            const response = await apiRequest('get', `/product-provider?type=vender&pageNo=${pageNo}&pageSize=100&${searchText ? `searchText=${searchText}` : ''}`);
            const { data } = response.data;
            if (data?.length > 0) {
                setIsVendorAvailable(true);
                const array = [...vendorData, ...data];
                const uniqueArray = [
                    ...new Set(
                        array.map((item) => JSON.stringify(item)),
                    ),
                ].map((item) => JSON.parse(item));
                setVendorData(uniqueArray);
                setVendorLoading(false);
            } else {
                setIsVendorAvailable(false);
                setVendorLoading(false);
            }
        } catch (error) {
            setVendorLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }


    const loadNextVendor = async (event) => {
        const listBoxNode = event.currentTarget;
        const numberOfPages = vendorPage + 1;
        const position = listBoxNode.scrollTop + listBoxNode.clientHeight;
        if (listBoxNode.scrollHeight - position <= 1 && isVendorAvailable) {
            setVendorPage(numberOfPages);
            await getVendorData(numberOfPages);
        }
    };

    const getBudget = async () => {
        try {
            setBudgetLoading(true);
            const response = await apiRequest('get', `/budget?projectId=${projectData?.project_id}&pageNo=1&pageSize=100`);
            const { data } = response.data;
            if (data?.length > 0) {
                setBudgetData(data);
            }
            setBudgetLoading(false);
        } catch (error) {
            setBudgetLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const getCostCentre = async () => {
        try {
            setCostLoading(true);
            const url = [
                `/department/cost-center?projectId=${projectData?.project_id}`,
                `pageNo=1`,
                `pageSize=100`,
            ].filter(Boolean).join('&');
            const response = await apiRequest('get', `${url}`);
            const { data } = response.data;
            setCostData(data);
            setCostLoading(false);
        } catch (error) {
            setCostLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const getPhase = async () => {
        try {
            setPhaseLoading(true);
            const url = [
                `/department/phases?projectId=${projectData?.project_id}`,
                `pageNo=1`,
                `pageSize=100`,
            ].filter(Boolean).join('&');
            const response = await apiRequest('get', `${url}`);
            const { data } = response.data;
            setPhaseData(data);
            setPhaseLoading(false);
        } catch (error) {
            setPhaseLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (projectData?.project_id) {
            getPhase();
            getCostCentre();
            getBudget();
            getVendorData(vendorPage);
        }
    }, [projectData]);

    const getManufacturer = async (searchText) => {
        try {
            setManufacturerLoading(true);
            const response = await apiRequest('get', `/product/product-by-category?category=${selectedData[0]?.product?.category_id}&subCategory=${selectedData[0]?.product?.sub_category_id}&${searchText ? `searchText=${searchText}` : ''}`);
            const { data } = response.data;
            if (data?.length > 0) {
                setManufacturerData(data);
                setManufacturerLoading(false);
            } else {
                setManufacturerLoading(false);
            }
        } catch (error) {
            setManufacturerLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (selectedData[0]?.product?.category_id && selectedData[0]?.product?.sub_category_id) {
            getManufacturer();
        }
    }, [selectedData]);

    const getModelData = async (searchText) => {
        try {
            setModelLoading(true);
            const response = await apiRequest('get', `/product/product-by-category?category=${selectedData[0]?.product?.category_id}&subCategory=${selectedData[0]?.product?.sub_category_id}&manufacturer=${itemData?.manufacturer_id}&${searchText ? `searchText=${searchText}` : ''}`);
            const { data } = response.data;
            if (data?.length > 0) {
                setModelData(data);
                setItemData((prevData) => ({
                    ...prevData,
                    product_data: {
                        ...prevData?.product_data,
                        model: data[0]?.model
                    },
                    product_id: data[0]?.product_id
                }));
                setModelLoading(false);
            } else {
                setModelLoading(false);
            }
        } catch (error) {
            setModelLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (itemData?.manufacturer_id) {
            getModelData();
        }
    }, [itemData?.manufacturer_id]);

    function removeNulls(obj) {
        if (obj === null || typeof obj !== 'object') return obj;

        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value === null) return acc;
            if (typeof value === 'object' && !Array.isArray(value)) {
                const nested = removeNulls(value);
                if (Object.keys(nested).length > 0) {
                    acc[key] = nested;
                }
            } else {
                acc[key] = value;
            }

            return acc;
        }, {});
    }

    // Single place to wipe both itemData and resetFields back to their
    // initial state. Called on successful save and on Cancel.
    const resetAllState = () => {
        setItemData({
            manufacturer_id: null,
            need_by_date: null,
            quantity: null,
            unit_price: null,
            funding_source: null,
            status: null,
            scope: null,
            product_id: null,
            notes: null,
            alternate_description: null,
            budget_id: null,
            phase_id: null,
            phase_date: null,
            bid_group_id: null,
            cost_centre_id: null,
            stage: null,
            product_data: {
                price_type: null,
                model: null,
                custom_id_1: null,
                custom_id_2: null,
                vendor_id: null,
                furnish_responsibility: null,
                install_responsibility: null,
                architectural_code: null
            }
        });
        setResetFields({ alternate_description: false });
        setIsConvert(false);
    };

    return (
        <>
            {dataLoading && <LoadingPage isLoading={dataLoading} />}
            <DemoBox1>
                <Typography variant='body1' style={{ fontWeight: 'bold' }}>
                    Update Fields
                </Typography>
                <Box>
                    <Button
                        variant='contained'
                        size='small'
                        onClick={async () => {
                            const data = removeNulls(itemData);

                            // Re-inject deliberately reset fields. removeNulls
                            // stripped them (their values are null), but a
                            // Reset toggle is a meaningful intent — the
                            // backend should receive `null` so it can clear
                            // the column. Switch to `""` here if your API
                            // wants an empty string instead.
                            Object.entries(resetFields).forEach(([key, isReset]) => {
                                if (isReset) data[key] = null;
                            });

                            // A pure "nothing touched + nothing reset" save
                            // is a no-op. Block it with the same message as
                            // before. Note we check the post-injection
                            // `data`, not just removeNulls' output.
                            if (Object.keys(data)?.length > 0) {
                                setDataLoading(true);
                                try {
                                    if (itemData?.need_by_date !== null) {
                                        data.need_by_date = moment(itemData?.need_by_date).format('YYYY-MM-DDThh:mm:ssZ')
                                    }
                                    if (itemData?.phase_date !== null) {
                                        data.phase_date = moment(itemData?.phase_date).format('YYYY-MM-DDThh:mm:ssZ')
                                    }
                                    if (itemData?.quantity !== null) {
                                        data.quantity = Number(itemData?.quantity)
                                    }
                                    if (itemData?.product_id !== null) {
                                        const responseProduct = await apiRequest('get', `/product/${itemData?.product_id}`);
                                        data.unit_price = Number(responseProduct?.data?.data?.list_price);
                                        if (isConvert) {
                                            data.product_data.model = responseProduct?.data?.data?.model;
                                        }
                                    } else if (itemData?.unit_price !== null) {
                                        data.unit_price = Number(itemData?.unit_price);
                                    }
                                    const response = await apiRequest('post', '/project-item/update-multiple', {
                                        item_ids: selectedData.map((a) => a.item_id),
                                        item_data: data
                                    });
                                    if (response.data) {
                                        setDataLoading(false);
                                        onGridReady();
                                        setItemOpen(false);
                                        resetAllState();
                                        showNotification(response?.data?.message, 'success');
                                    }
                                } catch (error) {
                                    setDataLoading(false);
                                    showNotification(error?.response?.data.error || error.message, 'error');
                                }
                            } else {
                                showNotification('Please enter any field value before proceed to update', 'error');
                            }
                        }}
                    >
                        Save
                    </Button>
                    <IconButton
                        onClick={() => {
                            setItemOpen(false);
                            resetAllState();
                        }}
                    >
                        <ClearIcon />
                    </IconButton>
                </Box>
            </DemoBox1>
            <Divider />
            <Grid container spacing={2} p={1}>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={uniqueManufacturers.find((c) => c?.product_provider_id === itemData?.manufacturer_id) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        clearIcon={null}
                        disabled={isConvert}
                        options={uniqueManufacturers}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                manufacturer_id: newValue?.product_provider_id || null,
                                manufacturer: {
                                    name: newValue?.name
                                }
                            }));
                        }}
                        onInputChange={(e) => {
                            if (e) {
                                if (e?.target?.value?.length >= 1) {
                                    setManufacturerData([]);
                                    getManufacturer(e?.target?.value);
                                }
                            }
                        }}
                        renderInput={(params) =>
                            <TextField
                                fullWidth
                                {...params}
                                label="Manufacturer"
                                name="Manufacturer"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {manufacturerLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={modelData.find((c) => c.model === itemData?.product_data?.model) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        clearIcon={null}
                        disabled={isConvert}
                        options={modelData}
                        getOptionLabel={(option) => option?.model}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                product_data: {
                                    ...prevData?.product_data,
                                    model: newValue?.model
                                },
                                product_id: newValue?.product_id
                            }));
                        }}
                        onInputChange={(e) => {
                            if (e) {
                                if (e?.target?.value?.length >= 1) {
                                    setModelData([]);
                                    getModelData(e?.target?.value);
                                }
                            }
                        }}
                        renderInput={(params) =>
                            <TextField
                                fullWidth
                                {...params}
                                label="Model"
                                name="Model"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {modelLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={vendorData.find((c) => c.product_provider_id === itemData?.product_data?.vendor_id) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={vendorData}
                        getOptionLabel={(option) => option?.name}
                        ListboxProps={{
                            onScroll: (event) => loadNextVendor(event),
                        }}
                        onChange={async (event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                product_data: {
                                    ...prevData?.product_data,
                                    vendor_id: newValue?.product_provider_id || null
                                },
                            }));
                        }}
                        onInputChange={(e) => {
                            if (e) {
                                if (e?.target?.value?.length >= 1) {
                                    setVendorData([]);
                                    setVendorPage(1);
                                    getVendorData(1, e?.target?.value);
                                }
                            }
                        }}
                        renderInput={(params) =>
                            <TextField
                                fullWidth
                                {...params}
                                label="Vendor"
                                name="Vendor"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {vendorLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={ItemStatus.find((c) => c.value === itemData?.status) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={ItemStatus}
                        clearIcon={null}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                status: newValue?.value || null
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Status" name="Status" variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={ActionStage.find((c) => c.value === itemData?.stage) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={ActionStage}
                        clearIcon={null}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                stage: newValue?.value || null
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Action Stage" name="ActionStage" variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox checked={isConvert} onClick={async (e) => {
                                setIsConvert(e.target.checked);
                                if (e.target.checked === true) {
                                    setDataLoading(true);
                                    const url = `/product?numaricSku=0&pageNo=1&pageSize=10&subCategory=${selectedData[0]?.product?.sub_category_id}`;
                                    try {
                                        const response = await apiRequest('get', `${url}`);
                                        if (response.data) {
                                            const { data } = response.data;
                                            setItemData((prevData) => ({
                                                ...prevData,
                                                manufacturer_id: null,
                                                manufacturer: {
                                                    name: null
                                                },
                                                product_data: {
                                                    ...prevData?.product_data,
                                                    model: 'UnSpecified',
                                                },
                                                product_id: data[0]?.product_id
                                            }));
                                            setDataLoading(false);
                                        }
                                    } catch (error) {
                                        setDataLoading(false);
                                        showNotification(error?.response?.data.error || error.message, 'error');
                                    }
                                }
                            }} />}
                            label="Convert To Default Product"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }}
                        />
                    </FormGroup>
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={PriceType.find((c) => c.value === itemData?.product_data?.price_type) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        clearIcon={null}
                        options={PriceType}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                product_data: {
                                    ...prevData.product_data,
                                    price_type: newValue?.value || null
                                }
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Price Type" name="Price Type" variant="outlined" />}
                    />
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Price</InputLabel>
                        <OutlinedInput
                            label="Price"
                            id="standard-adornment-password"
                            type='number'
                            value={itemData?.unit_price}
                            startAdornment={<InputAdornment position="start">{CurrencyJSON.find((a) => a.value === projectData?.currency)?.symbol}</InputAdornment>}
                            onChange={(e) => {
                                setItemData((prevData) => ({
                                    ...prevData,
                                    unit_price: e.target.value
                                }))
                            }}
                            onKeyDown={(event) => {
                                if (event.keyCode === 38 || event.keyCode === 40) {
                                    event.preventDefault();
                                }
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Quantity</InputLabel>
                        <OutlinedInput
                            label="Quantity"
                            id="standard-adornment-password"
                            type='number'
                            value={itemData?.quantity}
                            onChange={(e) => {
                                setItemData((prevData) => ({
                                    ...prevData,
                                    quantity: e.target.value
                                }));
                            }}
                            onKeyDown={(event) => {
                                if (event.keyCode === 38 || event.keyCode === 40) {
                                    event.preventDefault();
                                }
                            }}
                        />
                    </FormControl>
                </Grid>

                {/* Alternate Description with Reset toggle.
                    Ticking the checkbox sends `alternate_description: null`
                    on save (clears the column on the backend), and disables
                    the text input so the user can't accidentally type a new
                    value into a field they've just told the form to clear. */}
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.25,
                        }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                Alternate Description
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={resetFields.alternate_description}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setResetFields((p) => ({ ...p, alternate_description: checked }));
                                            // Clear the local input so the UI matches
                                            // what will be sent on save.
                                            if (checked) {
                                                setItemData((prev) => ({ ...prev, alternate_description: null }));
                                            }
                                        }}
                                        sx={{ p: 0.25 }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '11px' }}>Reset</Typography>}
                                sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '11px' } }}
                            />
                        </Box>
                        <OutlinedInput
                            label="Alternate Description"
                            id="alternate-description"
                            type='text'
                            disabled={resetFields.alternate_description}
                            value={
                                resetFields.alternate_description
                                    ? ''
                                    : (itemData?.alternate_description || '')
                            }
                            placeholder={resetFields.alternate_description ? 'Will be cleared on save' : ''}
                            onChange={(e) => {
                                setItemData((prevData) => ({
                                    ...prevData,
                                    alternate_description: e.target.value
                                }));
                            }}
                        />
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={FundingSource.find((c) => c.value === itemData?.funding_source) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        clearIcon={null}
                        options={FundingSource}
                        getOptionLabel={(option) => option.name}
                        onChange={(event, newValue) => {
                            setItemData((prevState) => ({
                                ...prevState,
                                funding_source: newValue?.value || null
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Funding Source" name="Funding Source" variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={budgetData.find((c) => c.budget_id === itemData?.budget_id) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={budgetData}
                        getOptionLabel={(option) => option?.budget_name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                budget_id: newValue?.budget_id || null
                            }));
                        }}
                        renderInput={(params) =>
                            <TextField
                                fullWidth
                                {...params}
                                label="Budget"
                                name="Budget"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {budgetLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={Responsibility.find((c) => c.value === itemData?.product_data?.furnish_responsibility) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={Responsibility}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                product_data: {
                                    ...prevData.product_data,
                                    furnish_responsibility: newValue?.value || null,
                                }
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Furnish Responsibility" name="Furnish Responsibility" variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={Responsibility.find((c) => c.value === itemData?.product_data?.install_responsibility) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={Responsibility}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                product_data: {
                                    ...prevData.product_data,
                                    install_responsibility: newValue?.value || null,
                                }
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Install Responsibility" name="Install Responsibility" variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={ArchitecturalCode.find((c) => c.value === itemData?.product_data?.architectural_code) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={ArchitecturalCode}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                product_data: {
                                    ...prevData.product_data,
                                    architectural_code: newValue?.value || null,
                                }
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Architectural Code" name="Architectural Code" variant="outlined" />}
                    />
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={costData.find((c) => c.cost_centre_id === itemData?.cost_centre_id) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={costData}
                        getOptionLabel={(option) => option.cost_centre_name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                cost_centre_id: newValue?.cost_centre_id || null,
                            }));
                        }}
                        renderInput={(params) =>
                            <TextField
                                fullWidth
                                {...params}
                                label="Cost Centre"
                                name="Cost Centre"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {costLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />}
                    />
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size='small' fullWidth variant='outlined'>
                        <InputLabel
                            htmlFor='standard-adornment-password'
                            shrink={
                                itemData?.need_by_date === null ||
                                itemData?.need_by_date === '' ||
                                itemData?.need_by_date
                            }
                        >
                            Need By Date
                        </InputLabel>
                        <OutlinedInput
                            label='Need By'
                            id='need by'
                            type='date'
                            value={itemData?.need_by_date || ''}
                            onChange={e => {
                                setItemData(prevData => ({
                                    ...prevData,
                                    need_by_date: e.target.value || null
                                }))
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={phaseData.find((c) => c.phase_id === itemData?.phase_id) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={phaseData}
                        getOptionLabel={(option) => option.phase_name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                phase_id: newValue?.phase_id || null,
                                phase_date: newValue?.phase_date ? moment(newValue?.phase_date).format('YYYY-MM-DD') : null
                            }));
                        }}
                        renderInput={(params) =>
                            <TextField
                                fullWidth
                                {...params}
                                label="Area/Phase Name"
                                name="Area/Phase Name"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {phaseLoading ? <CircularProgress color="primary" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />}
                    />
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password" shrink={itemData?.phase_date === null || itemData?.phase_date === '' || itemData?.phase_date}>Area/Phase Date</InputLabel>
                        <OutlinedInput
                            label="Area/Phase Date"
                            id="Area/Phase Date"
                            type='date'
                            value={itemData?.phase_date || ''}
                            onChange={(e) => {
                                setItemData((prevData) => ({
                                    ...prevData,
                                    phase_date: e.target.value || null,
                                }));
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={Scope.find((c) => c.value === itemData?.scope) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        options={Scope}
                        clearIcon={null}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                scope: newValue?.value || null,
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Scope" name="Scope" variant="outlined" />}
                    />
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Custom ID1</InputLabel>
                        <OutlinedInput
                            label="Custom ID1"
                            id="standard-adornment-password"
                            type='text'
                            value={itemData?.product_data?.custom_id_1 || ''}
                            onChange={(e) => {
                                setItemData((prevData) => ({
                                    ...prevData,
                                    product_data: {
                                        ...prevData?.product_data,
                                        custom_id_1: e.target.value
                                    }
                                }));
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <FormControl size="small" fullWidth variant="outlined">
                        <InputLabel htmlFor="standard-adornment-password">Custom ID2</InputLabel>
                        <OutlinedInput
                            label="Custom ID2"
                            id="standard-adornment-password"
                            type='text'
                            value={itemData?.product_data?.custom_id_2 || ''}
                            onChange={(e) => {
                                setItemData((prevData) => ({
                                    ...prevData,
                                    product_data: {
                                        ...prevData?.product_data,
                                        custom_id_2: e.target.value
                                    }
                                }));
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item sm={12} md={6} lg={3}>
                    <Autocomplete
                        value={[]?.find((c) => c.value === itemData?.bid_group_id) || null}
                        id="combo-box-demo"
                        size="small"
                        fullWidth
                        clearIcon={null}
                        options={[]}
                        getOptionLabel={(option) => option?.name}
                        onChange={(event, newValue) => {
                            setItemData((prevData) => ({
                                ...prevData,
                                bid_group_id: newValue?.value || null,
                            }));
                        }}
                        renderInput={(params) => <TextField fullWidth {...params} label="Bid Group" name="Bid Group" variant="outlined" />}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default UpdateItem